import React from "react";
import { motion } from "framer-motion";
import { Video, FileText, Mic, PenTool, BookOpen, Sparkles, Users, ArrowRight } from "lucide-react";

const FellowsThoughtLeaderContentSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #c4b5fd 50%, #93c5fd 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-20 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1100px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-sm font-semibold mb-4 border border-indigo-100/60 shadow-sm">
            <Sparkles className="w-4 h-4 text-indigo-500" />
            <span>Thought Leadership</span>
          </div>
          <h2 className="text-3xl lg:text-[40px] font-extrabold text-slate-900 tracking-tight leading-tight">
            Become the voice on{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
              AI in dentistry.
            </span>
          </h2>
          <p className="text-[16px] text-slate-500 font-medium mt-4 max-w-2xl mx-auto">
            Partner with our team to create content that helps the entire industry move forward.
          </p>
        </motion.div>

        {/* Two cards */}
        <div className="w-full grid grid-cols-2 gap-5 mb-5">
          {/* Card 1: Video / Article Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="rounded-[24px] overflow-hidden border border-white shadow-[0_20px_60px_-16px_rgba(0,0,0,0.08)] bg-white/80 backdrop-blur-xl relative"
          >
            <div className="absolute top-0 left-0 right-0 h-[4px]" style={{ background: "linear-gradient(90deg, #6366f1 0%, #4f46e5 100%)" }} />

            <div className="p-6">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center mb-4 shadow-lg shadow-indigo-500/20 border border-indigo-400">
                <Video className="w-7 h-7 text-white" />
              </div>

              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Bite-Sized Videos & Articles
              </h3>
              <p className="text-[14px] text-slate-500 leading-relaxed font-medium mb-4">
                Create short, impactful content (2-3 minutes) showing other offices how to leverage AI to transform their practice. Our team handles production — you bring the expertise.
              </p>

              <div className="space-y-3">
                {[
                  { icon: Video, label: "Short-form video walkthroughs (2-3 min)", delay: 0.4 },
                  { icon: FileText, label: "Practical articles on AI workflows", delay: 0.5 },
                  { icon: Users, label: "Content shared across Asha's practice network", delay: 0.6 },
                  { icon: Sparkles, label: "Our team handles editing & production", delay: 0.7 },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: item.delay, duration: 0.4 }}
                    className="flex items-center gap-3 px-3 py-2 rounded-xl bg-indigo-50/80 border border-indigo-100/60"
                  >
                    <item.icon className="w-4 h-4 text-indigo-600 shrink-0" />
                    <span className="text-[13px] font-semibold text-slate-700">{item.label}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Card 2: Co-authoring */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="rounded-[24px] overflow-hidden border border-white shadow-[0_20px_60px_-16px_rgba(0,0,0,0.08)] bg-white/80 backdrop-blur-xl relative"
          >
            <div className="absolute top-0 left-0 right-0 h-[4px]" style={{ background: "linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)" }} />

            <div className="p-6">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/20 border border-blue-400">
                <PenTool className="w-7 h-7 text-white" />
              </div>

              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Co-Author Articles & Studies
              </h3>
              <p className="text-[14px] text-slate-500 leading-relaxed font-medium mb-4">
                Interested in going deeper? We may invite you to co-author articles or studies on AI's impact in clinical settings — putting your name on work that shapes the industry.
              </p>

              <div className="space-y-3">
                {[
                  { icon: BookOpen, label: "Co-author published articles & whitepapers", delay: 0.5 },
                  { icon: Mic, label: "Contribute to clinical AI case studies", delay: 0.6 },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: item.delay, duration: 0.4 }}
                    className="flex items-center gap-3 px-3 py-2 rounded-xl bg-blue-50/80 border border-blue-100/60"
                  >
                    <item.icon className="w-4 h-4 text-blue-600 shrink-0" />
                    <span className="text-[13px] font-semibold text-slate-700">{item.label}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

      </div>
    </div>
  );
};

export default FellowsThoughtLeaderContentSlide;
